// ButterflyGames SDK orchestrator. Owns lifecycle and exposes a small,
// stable surface that game code calls. Subsystems are composed here.

import { resolveConfig } from './config.js';
import { createLogger } from './logger.js';
import { Emitter } from './events.js';
import { AdManager } from '../ads/adManager.js';
import { StorageManager } from '../storage/storageManager.js';
import { LeaderboardManager } from '../leaderboard/leaderboardManager.js';
import { SupabaseBackend } from '../backend/supabaseBackend.js';
import { BridgeScreen } from '../ui/bridgeScreen.js';
import { AnalyticsManager } from '../analytics/analyticsManager.js';
import { platformName, isCapacitorNative, capacitorPlatform } from '../platform/env.js';

export class ButterflyGamesSDK {
  constructor(userConfig = {}) {
    this.config = resolveConfig(userConfig);
    this.log = createLogger('ButterflySDK', this.config.logLevel);
    this.events = new Emitter();
    this.platform = platformName();
    this._ready = false;
    this._bridge = null;
    this._startPromise = null;   // set on first start(); makes start() idempotent
    this._lastBannerPlacement = 'banner';

    // Audio/focus lifecycle. Effective state is "muted/paused if ANY source asks
    // for it" â€” so loading + tab-hidden overlap cleanly without flapping.
    this._muteSources = new Set();
    this._pauseSources = new Set();
    this._muted = false;
    this._paused = false;
    this._visibilityBound = false;

    const emit = (e, ...a) => this.events.emit(e, ...a);

    // Shared backend (one client + one player identity for every subsystem).
    this.backend = this.config.backend.provider === 'supabase'
      ? new SupabaseBackend(this.config.backend, this.config.identity, { log: this.log, emit })
      : null;

    // When a backend is configured, "auto" adapters default to it.
    if (this.backend) {
      if (this.config.storage.adapter === 'auto') this.config.storage.adapter = 'supabase';
      if (this.config.leaderboard.adapter === 'auto') this.config.leaderboard.adapter = 'supabase';
    }

    const ctx = { log: this.log, emit, backend: this.backend };

    this.ads = new AdManager(this.config.ads, ctx);
    this.storage = new StorageManager(
      this.config.storage, { ...ctx, migrate: userConfig.onMigrate },
      this.config.gameId, this.config.version
    );
    this.leaderboard = new LeaderboardManager(this.config.leaderboard, ctx, this.storage);

    // Analytics stream id: "<gameId>-web" | "<gameId>-android" | "<gameId>-ios".
    const analyticsPlatform = isCapacitorNative() ? capacitorPlatform() : 'web';
    this.analyticsAppId = `${this.config.gameId}-${analyticsPlatform}`;
    this.analytics = new AnalyticsManager(this.config.analytics, {
      log: this.log, appId: this.analyticsAppId, platform: analyticsPlatform, version: this.config.version,
    });
  }

  on(event, handler) { return this.events.on(event, handler); }
  once(event, handler) { return this.events.once(event, handler); }

  // Call once at game boot. Shows the bridge screen, restores any existing
  // Google session, inits ads, then hands control back. If no session exists,
  // the game should prompt sign-in via signInWithGoogle().
  async init() {
    if (this._ready) return this;
    this.log.info(`Booting on "${this.platform}" for game "${this.config.gameId}".`);

    // Mute while the splash loads; bind tab focus/visibility handling.
    if (this.config.lifecycle.muteWhileLoading) this._setMute('loading', true);
    if (this.config.lifecycle.autoPauseOnHidden) this._bindVisibility();
    // Pause + mute while an ad is on screen (only when one actually opens), then
    // restore â€” without overriding the player's explicit 'user' choice.
    if (this.config.lifecycle.pauseDuringAds) {
      this.on('ad:open', () => { this._setPause('ad', true); this._setMute('ad', true); });
      this.on('ad:close', () => { this._setPause('ad', false); this._setMute('ad', false); });
    }

    if (this.config.bridge.enabled) {
      this._bridge = new BridgeScreen(this.config.bridge, { log: this.log });
      this._bridge.show();
    }

    // Backend identity first, so saves/leaderboards are usable immediately after init.
    if (this.backend) {
      await this.backend.init().catch((err) => this.log.warn('Backend init issue', err));
      this._wireSignInGate();
    }

    await this.ads.init().catch((err) => this.log.warn('Ad init issue', err));
    await this.analytics.init().catch((err) => this.log.warn('Analytics init issue', err));

    this._ready = true;
    this.events.emit('sdk:ready');
    return this;
  }

  isReady() { return this._ready; }

  // Drive the bridge's Google sign-in button from auth state. Shows the button
  // when no session exists, and flips to "signed in" when the player returns
  // from the Google redirect (or was already signed in).
  _wireSignInGate() {
    if (!this._bridge || this.config.bridge.requireSignIn === false) return;

    // Embedded in the Butterfly website: the parent handles sign-in via SSO, so
    // never show the in-game Google button or trigger an OAuth redirect. Just
    // reflect whatever identity the parent forwards.
    if (this.backend?.embedded) {
      if (this.isSignedIn()) this._bridge.setSignedIn(this.getPlayer());
      this.on('player:change', (player) => {
        if (player.signedIn) this._bridge?.setSignedIn(player);
      });
      return;
    }

    if (this.isSignedIn()) {
      this._bridge.setSignedIn(this.getPlayer());
    } else {
      this._bridge.showSignIn({ onSignIn: () => this.signInWithGoogle() });
    }
    this.on('player:change', (player) => {
      if (player.signedIn) this._bridge?.setSignedIn(player);
      else this._bridge?.showSignIn({ onSignIn: () => this.signInWithGoogle() });
    });
  }

  // Drive the splash progress (0..1) from your asset loader. Works for both
  // 'bar' and 'icon-fill' modes. Progress is monotonic â€” values that would move
  // it backward are ignored â€” and there is no implicit/fallback progress.
  reportLoadProgress(value) {
    this.log.debug('[sdk] progress value received', value);
    this._bridge?.setProgress(value);
  }

  // Call when your game is fully loaded and ready to play. Dismisses the bridge
  // (snapping progress to 100% first), then resolves. Idempotent: repeat calls
  // share the same run, and it resolves safely even if the bridge is already
  // gone or was never shown. Works immediately after init().
  start() {
    if (this._startPromise) return this._startPromise;
    this._startPromise = (async () => {
      this.log.debug('[sdk] start called');
      if (this._bridge) {
        await this._bridge.dismiss();
        this._bridge = null;
      } else {
        this.log.debug('[sdk] start: no bridge to dismiss');
      }
      // Splash done â†’ lift the loading mute (game sound may play now, unless the
      // tab is hidden or the game called mute() itself).
      this._setMute('loading', false);
      this.events.emit('sdk:start');
      return this;
    })();
    return this._startPromise;
  }

  // ---- Audio / focus lifecycle -----------------------------------------
  // The SDK emits events; your game does the actual muting/pausing:
  //   bf.on('mute',   () => audio.muteAll());
  //   bf.on('unmute', () => audio.unmuteAll());
  //   bf.on('pause',  () => game.pause());
  //   bf.on('resume', () => game.resume());
  //
  // The 'user' source is the player's explicit choice from YOUR UI. Because
  // state is "active while any source asks", an ad/tab-hidden mute won't undo a
  // user mute: after the ad closes the SDK only clears its own 'ad' source, so
  // if the user had muted/paused, it stays that way.
  mute()   { this._setMute('user', true); }
  unmute() { this._setMute('user', false); }
  pause()  { this._setPause('user', true); }
  resume() { this._setPause('user', false); }
  // Boolean form â€” call from your in-game sound/pause toggle so the SDK knows
  // the player's explicit intent.
  setUserMuted(on)  { this._setMute('user', !!on); }
  setUserPaused(on) { this._setPause('user', !!on); }
  isMuted()  { return this._muted; }
  isPaused() { return this._paused; }

  _setMute(source, on) {
    if (on) this._muteSources.add(source); else this._muteSources.delete(source);
    const muted = this._muteSources.size > 0;
    if (muted === this._muted) return;
    this._muted = muted;
    this.log.debug(`[sdk] ${muted ? 'mute' : 'unmute'}`, [...this._muteSources]);
    this.events.emit(muted ? 'mute' : 'unmute', { sources: [...this._muteSources] });
  }

  _setPause(source, on) {
    if (on) this._pauseSources.add(source); else this._pauseSources.delete(source);
    const paused = this._pauseSources.size > 0;
    if (paused === this._paused) return;
    this._paused = paused;
    this.log.debug(`[sdk] ${paused ? 'pause' : 'resume'}`, [...this._pauseSources]);
    this.events.emit(paused ? 'pause' : 'resume', { sources: [...this._pauseSources] });
  }

  // Pause + mute when the tab is hidden; resume + unmute when it returns.
  _bindVisibility() {
    if (this._visibilityBound) return;
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;
    this._visibilityBound = true;
    this._onVisibility = () => {
      const hidden = document.visibilityState === 'hidden' || document.hidden === true;
      this._setPause('visibility', hidden);
      this._setMute('visibility', hidden);
    };
    document.addEventListener('visibilitychange', this._onVisibility);
  }

  // ---- Analytics -------------------------------------------------------
  // bf.analytics.event('Game started') also works (analytics is the manager).
  logEvent(name, props) { return this.analytics.event(name, props); }

  // ---- Ads -------------------------------------------------------------
  // Each ad method auto-emits analytics (ad_requested / ad_shown / ad_failed /
  // ad_closed, plus ad_reward_granted|denied for rewarded) via the analytics
  // manager, so web and Android get identical ad telemetry without per-game code.
  // All analytics is best-effort and never alters the ad result or throws.
  async showInterstitial(placement = 'default') {
    return this._trackAd('interstitial', placement, () => this.ads.showInterstitial(placement));
  }
  async showRewarded(placement = 'default') {
    return this._trackAd('rewarded', placement, () => this.ads.showRewarded(placement), true);
  }
  async showBanner(placement = 'banner') {              // native (Capacitor/AdMob)
    this._lastBannerPlacement = placement;
    this._adEvent('ad_requested', { adType: 'banner', placement });
    let result;
    try { result = await this.ads.showBanner(placement); }
    catch (err) { this._adEvent('ad_failed', { adType: 'banner', placement, reason: 'error', error: String(err?.message || err), value: 0 }); return { shown: false, reason: 'error' }; }
    if (result?.shown) this._adEvent('ad_shown', { adType: 'banner', placement, provider: result.provider, value: 1 });
    else this._adEvent('ad_failed', { adType: 'banner', placement, provider: result?.provider, reason: result?.reason || 'no-fill', value: 0 });
    return result;
  }
  async hideBanner(placement = this._lastBannerPlacement || 'banner') {
    let result;
    try { result = await this.ads.hideBanner(placement); }
    catch (err) { return { hidden: false, reason: 'error' }; }
    if (result?.hidden) this._adEvent('ad_closed', { adType: 'banner', placement, value: 1 });
    return result;
  }

  // Wrap an interstitial/rewarded call with lifecycle analytics. The ad result
  // is returned unchanged; analytics failures are swallowed.
  async _trackAd(adType, placement, run, rewarded = false) {
    this._adEvent('ad_requested', { adType, placement });
    let result;
    try {
      result = await run();
    } catch (err) {
      this._adEvent('ad_failed', { adType, placement, reason: 'error', error: String(err?.message || err), value: 0 });
      return rewarded ? { shown: false, granted: false, reason: 'error' } : { shown: false, reason: 'error' };
    }
    const provider = result?.provider;
    if (result?.shown) {
      this._adEvent('ad_shown', { adType, placement, provider, value: 1 });
      if (rewarded) {
        if (result.granted) this._adEvent('ad_reward_granted', { adType, placement, provider, value: 1 });
        else this._adEvent('ad_reward_denied', { adType, placement, provider, value: 0 });
      }
      // Our interstitial/rewarded providers resolve after the ad is dismissed.
      this._adEvent('ad_closed', { adType, placement, provider, value: 1 });
    } else {
      this._adEvent('ad_failed', { adType, placement, provider, reason: result?.reason || 'no-fill', value: 0 });
    }
    return result;
  }

  // Emit an analytics event, dropping undefined props; never throws.
  _adEvent(name, props) {
    try {
      const clean = {};
      for (const [k, v] of Object.entries(props)) if (v !== undefined) clean[k] = v;
      this.analytics.event(name, clean);
    } catch { /* analytics must never break ads */ }
  }

  // ---- Save progress ---------------------------------------------------
  save(key, data) { return this.storage.save(key, data); }
  load(key, fallback) { return this.storage.load(key, fallback); }
  clearSave(key) { return this.storage.remove(key); }

  // ---- Leaderboards ----------------------------------------------------
  submitScore(boardId, entry) { return this.leaderboard.submit(boardId, entry); }
  getTopScores(boardId, limit) { return this.leaderboard.top(boardId, limit); }

  // ---- Player identity (shared across all Butterfly games) -------------
  getPlayer() {
    return this.backend?.getPlayer() || {
      playerId: null, displayName: this.config.identity.defaultName, signedIn: false,
    };
  }
  isSignedIn() { return !!this.backend?.identity.signedIn; }
  isEmbedded() { return !!this.backend?.embedded; }   // running in the Butterfly website iframe
  setPlayerName(name) { return this.backend?.setDisplayName(name); }
  signInWithGoogle() { return this.backend?.signInWithGoogle(); }   // redirects to Google
  signOut() { return this.backend?.signOut(); }
}



